/**
 * List View Indicator for Conditional Blocks
 *
 * Adds the Conditional Blocks logo to blocks in the List View panel
 * that have visibility conditions applied.
 */

import { isLegacyConditions, ConditionalBlocksLogo } from './utils/helpers.js';

const { subscribe, select } = wp.data;
const { createRoot, createElement } = wp.element;

// Cache of block clientIds that have conditions
let conditionsCache = new Map();

// Track React roots for cleanup
const reactRoots = new Map();

// MutationObserver instance
let listViewObserver = null;

// Debounce timer
let debounceTimer = null;

/**
 * Check if a single block has conditions applied.
 *
 * @param {Object} block The block object.
 * @return {boolean} True if block has conditions.
 */
function blockHasConditions(block) {
	if (!block || !block.attributes) {
		return false;
	}

	const { conditionalBlocks, conditionalBlocksAttributes } = block.attributes;

	const hasConditions = !!(
		Array.isArray(conditionalBlocks?.conditions) &&
		conditionalBlocks?.conditions.length >= 1
	);

	const hasSets = !!(
		Array.isArray(conditionalBlocks?.sets) &&
		conditionalBlocks?.sets.length >= 1
	);

	const hasLegacyCondition = isLegacyConditions(conditionalBlocksAttributes);

	return hasConditions || hasSets || hasLegacyCondition;
}

/**
 * Check if a block has conditions by looking up the store directly.
 * This is faster than using the cache for immediate lookups.
 *
 * @param {string} clientId The block's client ID.
 * @return {boolean} True if block has conditions.
 */
function getBlockHasConditions(clientId) {
	const block = select('core/block-editor').getBlock(clientId);
	return blockHasConditions(block);
}

/**
 * Recursively get all blocks including inner blocks.
 *
 * @param {Array} blocks Array of blocks.
 * @param {Array} result Accumulator array.
 * @return {Array} Flat array of all blocks.
 */
function getAllBlocksFlat(blocks, result = []) {
	for (const block of blocks) {
		result.push(block);
		if (block.innerBlocks?.length) {
			getAllBlocksFlat(block.innerBlocks, result);
		}
	}
	return result;
}

/**
 * Rebuild the cache of blocks that have conditions.
 */
function rebuildConditionsCache() {
	const newCache = new Map();
	const blocks = select('core/block-editor').getBlocks();
	const allBlocks = getAllBlocksFlat(blocks);

	for (const block of allBlocks) {
		if (blockHasConditions(block)) {
			newCache.set(block.clientId, true);
		}
	}

	conditionsCache = newCache;
}

/**
 * Create and inject the indicator icon into a list view row.
 *
 * @param {Element} row The list view row element.
 * @param {string} clientId The block's client ID.
 */
function injectIndicator(row, clientId) {
	// Check store directly for instant response (bypasses debounced cache)
	if (!getBlockHasConditions(clientId)) {
		// Remove indicator if it exists but block no longer has conditions
		removeIndicator(row);
		return;
	}

	// Check if indicator already exists
	if (row.querySelector('.conditional-blocks-list-view-indicator')) {
		return;
	}

	// Find the label wrapper where we'll append our icon
	const labelWrapper = row.querySelector(
		'.block-editor-list-view-block-select-button__label-wrapper'
	);

	if (!labelWrapper) {
		return;
	}

	// Create container for our indicator
	const indicatorContainer = document.createElement('span');
	indicatorContainer.className = 'conditional-blocks-list-view-indicator';
	indicatorContainer.title = 'Has Conditional Blocks visibility conditions';

	// Append to label wrapper (after the block name)
	labelWrapper.appendChild(indicatorContainer);

	// Render the React component
	const root = createRoot(indicatorContainer);
	root.render(createElement(ConditionalBlocksLogo, { width: 14 }));

	// Store root for cleanup
	reactRoots.set(indicatorContainer, root);
}

/**
 * Remove the indicator from a list view row.
 *
 * @param {Element} row The list view row element.
 */
function removeIndicator(row) {
	const indicator = row.querySelector('.conditional-blocks-list-view-indicator');
	if (indicator) {
		// Clean up React root
		const root = reactRoots.get(indicator);
		if (root) {
			root.unmount();
			reactRoots.delete(indicator);
		}
		indicator.remove();
	}
}

/**
 * Process all visible list view rows.
 */
function updateAllListViewRows() {
	const listViewRows = document.querySelectorAll(
		'.block-editor-list-view-leaf[data-block]'
	);

	listViewRows.forEach((row) => {
		const clientId = row.getAttribute('data-block');
		if (clientId) {
			injectIndicator(row, clientId);
		}
	});
}

/**
 * Handle mutations in the List View.
 *
 * @param {MutationRecord[]} mutations Array of mutation records.
 */
function handleMutations(mutations) {
	for (const mutation of mutations) {
		// Check added nodes for list view rows
		mutation.addedNodes.forEach((node) => {
			if (node.nodeType !== Node.ELEMENT_NODE) {
				return;
			}

			// Check if the node itself is a list view row
			if (
				node.classList?.contains('block-editor-list-view-leaf') &&
				node.hasAttribute('data-block')
			) {
				const clientId = node.getAttribute('data-block');
				if (clientId) {
					injectIndicator(node, clientId);
				}
			}

			// Check for list view rows within the added node
			const rows = node.querySelectorAll?.(
				'.block-editor-list-view-leaf[data-block]'
			);
			rows?.forEach((row) => {
				const clientId = row.getAttribute('data-block');
				if (clientId) {
					injectIndicator(row, clientId);
				}
			});
		});
	}
}

/**
 * Set up the MutationObserver for the List View.
 */
function setupObserver() {
	// Disconnect existing observer
	if (listViewObserver) {
		listViewObserver.disconnect();
	}

	// Create new observer
	listViewObserver = new MutationObserver(handleMutations);

	// Observe the editor wrapper for list view changes
	const editorWrapper = document.querySelector('.editor-editor-interface');
	if (editorWrapper) {
		listViewObserver.observe(editorWrapper, {
			childList: true,
			subtree: true,
		});
	}
}

/**
 * Debounced update function.
 */
function debouncedUpdate() {
	if (debounceTimer) {
		clearTimeout(debounceTimer);
	}

	debounceTimer = setTimeout(() => {
		rebuildConditionsCache();
		updateAllListViewRows();
	}, 100);
}

/**
 * Initialize the List View indicator system.
 */
export function initListViewIndicator() {
	// Initial setup
	rebuildConditionsCache();
	setupObserver();
	updateAllListViewRows();

	// Subscribe to store changes
	subscribe(() => {
		debouncedUpdate();
	});

	// Re-setup observer if editor interface changes (e.g., switching between visual/code editor)
	const checkEditorInterface = () => {
		const editorWrapper = document.querySelector('.editor-editor-interface');
		if (editorWrapper && !listViewObserver) {
			setupObserver();
			updateAllListViewRows();
		}
	};

	// Periodic check for editor interface (handles late initialization)
	const intervalId = setInterval(() => {
		checkEditorInterface();
	}, 1000);

	// Clean up interval after 10 seconds (editor should be loaded by then)
	setTimeout(() => {
		clearInterval(intervalId);
	}, 10000);
}
